import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bookmark, Download, Pause, Play, RefreshCw, Share2 } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { FilterBar } from "@/components/logs/FilterBar";
import { LogStats } from "@/components/logs/LogStats";
import { LogTable } from "@/components/logs/LogTable";
import { LogDetailDrawer } from "@/components/logs/LogDetailDrawer";
import { SaveViewDialog } from "@/components/logs/SaveViewDialog";
import { useUrlFilters } from "@/hooks/useUrlFilters";
import { useLogStream } from "@/hooks/useLogStream";
import { buildLogQueryParams, useLogQuery } from "@/hooks/useLogQuery";
import { getToken } from "@/lib/api";
import { useSuggestions } from "@/hooks/useSuggestions";
import { useSavedViews } from "@/hooks/useSavedViews";
import { useAuth } from "@/hooks/useAuth";
import { t, dateLocale } from "@/lib/i18n";
import type { LogEntry } from "@/lib/types";

export function LogsPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { filters, patchFilters, resetFilters } = useUrlFilters();
  const suggestions = useSuggestions(!!token);

  // Zeitraum gesetzt → REST-Suche in der Historie, Live-Stream aus.
  const isQuery = !!filters.range;
  const stream = useLogStream(filters, !!token && !isQuery);
  const query = useLogQuery(filters, !!token && isQuery);
  const logs = isQuery ? query.logs : stream.logs;

  const savedViews = useSavedViews();

  const [selected, setSelected] = useState<LogEntry | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const shareUrl = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch {
      // Clipboard blockiert (z.B. unsicherer Context) — leise weg
    }
  }, []);

  const exportLogs = useCallback(
    async (format: "csv" | "json") => {
      setExportOpen(false);
      setExporting(true);
      setExportError(null);
      try {
        const p = buildLogQueryParams(filters);
        p.set("limit", "50000");
        p.set("format", format);
        const token = getToken();
        const resp = await fetch(`/api/logs/export?${p}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `leif-logs-${new Date()
          .toISOString()
          .slice(0, 19)
          .replace(/[T:]/g, "-")}.${format}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (e) {
        setExportError(e instanceof Error ? e.message : String(e));
      } finally {
        setExporting(false);
      }
    },
    [filters],
  );

  const description = isQuery
    ? query.loading
      ? t("Zeitraum-Suche · lade…")
      : query.error
        ? t("Zeitraum-Suche · Fehler: {error}", { error: query.error })
        : t("Zeitraum-Suche · {n} Treffer", {
            n: (query.total ?? logs.length).toLocaleString(dateLocale()),
          })
    : stream.paused
      ? stream.missed > 0
        ? t("Pausiert · {n} neue Events seit Pause", {
            n: stream.missed.toLocaleString(dateLocale()),
          })
        : t("Pausiert")
      : stream.state === "open"
        ? stream.lastUpdate
          ? t("Live-Stream · letzte Aktualisierung {time}", {
              time: stream.lastUpdate.toLocaleTimeString(dateLocale()),
            })
          : t("Live-Stream")
        : stream.state === "connecting"
          ? t("Verbinde…")
          : t("Verbindung getrennt — versuche erneut");

  return (
    <div>
      <PageHeader
        title={t("Logs")}
        description={description}
        actions={
          <>
            {isQuery ? (
              <button
                type="button"
                onClick={query.refresh}
                disabled={query.loading}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-2 text-xs font-medium md:px-2.5 md:py-1.5 hover:bg-muted disabled:opacity-60"
              >
                <RefreshCw className={"h-3.5 w-3.5 " + (query.loading ? "animate-spin" : "")} />
                {t("Aktualisieren")}
              </button>
            ) : (
              <button
                type="button"
                onClick={stream.paused ? stream.resume : stream.pause}
                className={
                  "inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-xs font-medium md:px-2.5 md:py-1.5 " +
                  (stream.paused
                    ? "border-warning/50 bg-warning/10 text-warning hover:bg-warning/20"
                    : "border-border bg-card hover:bg-muted")
                }
                title={
                  stream.paused
                    ? t("Live-Stream fortsetzen (holt den aktuellen Stand)")
                    : t("Anzeige einfrieren — der Stream zählt im Hintergrund weiter")
                }
              >
                {stream.paused ? (
                  <>
                    <Play className="h-3.5 w-3.5" /> {t("Fortsetzen")}
                    {stream.missed > 0 ? (
                      <span className="ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-warning px-1 text-[10px] font-medium text-warning-foreground">
                        {stream.missed > 999 ? "999+" : stream.missed}
                      </span>
                    ) : null}
                  </>
                ) : (
                  <>
                    <Pause className="h-3.5 w-3.5" /> {t("Pause")}
                  </>
                )}
              </button>
            )}
            <button
              type="button"
              onClick={() => setSaveOpen(true)}
              title={t("Sicht speichern")}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-2 text-xs font-medium md:px-2.5 md:py-1.5 hover:bg-muted"
            >
              <Bookmark className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{t("Sicht speichern")}</span>
            </button>
            <button
              type="button"
              onClick={shareUrl}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-2 text-xs font-medium md:px-2.5 md:py-1.5 hover:bg-muted"
              title={t("URL mit aktuellen Filtern kopieren")}
            >
              <Share2 className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{t("Link kopieren")}</span>
            </button>
            <div className="relative">
              <button
                type="button"
                onClick={() => setExportOpen((v) => !v)}
                disabled={exporting}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-2 text-xs font-medium md:px-2.5 md:py-1.5 hover:bg-muted disabled:opacity-60"
                title={t("Logs mit aktuellen Filtern exportieren (max. 50.000 Zeilen)")}
              >
                <Download className="h-3.5 w-3.5" />{" "}
                <span className="hidden sm:inline">{exporting ? t("Exportiere…") : t("Export")}</span>
              </button>
              {exportOpen ? (
                <div className="absolute right-0 top-full z-20 mt-1 w-28 rounded-md border border-border bg-card p-1 shadow-md">
                  <button
                    type="button"
                    onClick={() => void exportLogs("csv")}
                    className="block w-full rounded px-2 py-1 text-left text-xs hover:bg-muted"
                  >
                    CSV
                  </button>
                  <button
                    type="button"
                    onClick={() => void exportLogs("json")}
                    className="block w-full rounded px-2 py-1 text-left text-xs hover:bg-muted"
                  >
                    JSON
                  </button>
                </div>
              ) : null}
            </div>
          </>
        }
      />

      {exportError ? (
        <div className="px-3 pt-2 text-xs text-destructive md:px-6">
          {t("Export fehlgeschlagen: {error}", { error: exportError })}
        </div>
      ) : null}

      <div className="px-3 py-3 md:px-6 md:py-4">
        <FilterBar
          filters={filters}
          onChange={patchFilters}
          onReset={resetFilters}
          suggestions={suggestions}
        />
        <LogStats logs={logs} />
        <LogTable
          logs={logs}
          onSelect={setSelected}
          selectedId={selected?.id ?? null}
          highlight={filters.search}
        />
      </div>

      <LogDetailDrawer
        log={selected}
        onClose={() => setSelected(null)}
        onFilterByClient={(client) => {
          patchFilters({ client });
          setSelected(null);
        }}
        onOpenTrace={(traceId) => {
          setSelected(null);
          navigate(`/traces/${encodeURIComponent(traceId)}`);
        }}
      />

      <SaveViewDialog
        open={saveOpen}
        filters={filters}
        onClose={() => setSaveOpen(false)}
        onSave={(name) => savedViews.create(name, filters)}
      />
    </div>
  );
}
